<template>
  <div class="payments-container">
    <div class="header-section">
      <h2>Payments Management</h2>
      <div class="stats-container">
        <div class="stat-card">
          <span class="stat-label">Total Payments</span>
          <span class="stat-value">{{ totalItems }}</span>
        </div>
        <div class="stat-card">
          <span class="stat-label">Selected</span>
          <span class="stat-value">{{ selectedItems.length }}</span>
        </div>
      </div>
    </div>
    
    <div class="table-container">
      <div class="filters-section">
        <div class="filter-group">
          <div class="filter-item">
            <label>Date Range</label>
            <div class="date-range">
              <input 
                type="date" 
                v-model="filters.startDate" 
                class="filter-input"
                @change="applyFilters"
              >
              <span>to</span>
              <input 
                type="date" 
                v-model="filters.endDate" 
                class="filter-input"
                @change="applyFilters"
              >
            </div>
          </div>

          <div class="filter-item">
            <label>Status</label>
            <select 
              v-model="filters.status" 
              class="filter-input"
              @change="applyFilters"
            >
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div class="filter-item">
            <label>Amount Range</label>
            <div class="amount-range">
              <input 
                type="number" 
                v-model="filters.minAmount" 
                placeholder="Min"
                class="filter-input"
                @change="applyFilters"
              >
              <span>to</span>
              <input 
                type="number" 
                v-model="filters.maxAmount" 
                placeholder="Max"
                class="filter-input"
                @change="applyFilters"
              >
            </div>
          </div>

          <div class="filter-item">
            <label>Platform</label>
            <select 
              v-model="filters.platform" 
              class="filter-input"
              @change="applyFilters"
            >
              <option value="">All Platforms</option>
              <option value="ios">iOS</option>
              <option value="android">Android</option>
              <option value="web">Web</option>
            </select>
          </div>

          <div class="filter-item">
            <label>Instructor</label>
            <select 
              v-model="filters.instructor" 
              class="filter-input"
              @change="applyFilters"
              :disabled="loadingInstructors"
            >
              <option value="">All Instructors</option>
              <option 
                v-for="instructor in instructors" 
                :key="instructor.id" 
                :value="instructor.id"
              >
                {{ instructor.first_name }} {{ instructor.last_name }}
              </option>
            </select>
            <div v-if="loadingInstructors" class="loading-indicator">
              Loading instructors...
            </div>
          </div>
        </div>

        <button 
          class="clear-filters-btn"
          @click="clearFilters"
          :disabled="!hasActiveFilters"
        >
          Clear Filters
        </button>
      </div>

      <div class="table-controls">
        <div class="search-box">
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Search payments..." 
            @input="handleSearch"
            class="search-input"
          >
          <span class="search-icon">🔍</span>
        </div>
      </div>

      <div class="table-responsive">
        <table class="payments-table">
          <thead>
            <tr>
              <th v-for="header in headers" 
                  :key="header.value"
                  @click="sortTable(header.value)"
                  :class="{ 'sortable': header.sortable !== false, 'sorted': sortBy === header.value }"
                  class="sort-header">
                {{ header.text }}
                <span v-if="header.sortable !== false" class="sort-icon">
                  {{ sortBy === header.value ? (sortType === 'asc' ? '↑' : '↓') : '↕' }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td :colspan="headers.length" class="loading-row">
                <div class="loading-spinner"></div>
                <span>Loading payments...</span>
              </td>
            </tr>
            <tr v-else-if="filteredPayments.length === 0">
              <td :colspan="headers.length" class="empty-row">
                No payments found
              </td>
            </tr>
            <tr v-else v-for="payment in filteredPayments" 
                :key="payment.id" 
                :class="{ 'selected': selectedItems.includes(payment) }"
                @click="toggleSelection(payment)">
              <td>{{ payment.id }}</td>
              <td>
                <div class="user-info">
                  <div class="user-name">{{ payment.user.first_name }} {{ payment.user.last_name }}</div>
                  <small class="user-email">{{ payment.user.email }}</small>
                </div>
              </td>
              <td>{{ payment.item_type }}</td>
              <td class="amount-value">${{ parseFloat(payment.amount).toFixed(2) }}</td>
              <td class="date-value">{{ new Date(payment.purchase_date).toLocaleDateString() }}</td>
              <td>{{ payment.platform }}</td>
              <td>
                <span :class="['status-badge', payment.status]">{{ payment.status }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div class="pagination" v-if="totalItems > 0">
        <div class="pagination-controls">
          <button 
            class="pagination-btn" 
            :disabled="serverOptions.page === 1"
            @click="changePage(serverOptions.page - 1)">
            Previous
          </button>
          <span class="page-info">
            Page {{ serverOptions.page }} of {{ Math.ceil(totalItems / serverOptions.rowsPerPage) }}
          </span>
          <button 
            class="pagination-btn" 
            :disabled="serverOptions.page * serverOptions.rowsPerPage >= totalItems"
            @click="changePage(serverOptions.page + 1)">
            Next
          </button>
        </div>
        <div class="per-page-selector">
          <label for="per-page">Items per page:</label>
          <select 
            id="per-page" 
            v-model="serverOptions.rowsPerPage" 
            @change="handlePerPageChange"
            class="per-page-select"
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="200">200</option>
            <option value="500">500</option>
            <option value="1000">1000</option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { ref, onMounted, computed } from 'vue';

export default {
  name: 'PaymentsComponent',
  setup() {
    const payments = ref([]);
    const loading = ref(true);
    const selectedItems = ref([]);
    const totalItems = ref(0);
    const searchQuery = ref('');
    const sortBy = ref('purchase_date');
    const sortType = ref('desc');
    const serverOptions = ref({
      page: 1,
      rowsPerPage: 10,
      sortBy: 'purchase_date',
      sortType: 'desc'
    });

    const headers = [
      { text: 'ID', value: 'id', sortable: true },
      { text: 'User', value: 'user', sortable: true },
      { text: 'Type', value: 'item_type', sortable: true },
      { text: 'Amount', value: 'amount', sortable: true },
      { text: 'Purchase Date', value: 'purchase_date', sortable: true },
      { text: 'Platform', value: 'platform', sortable: true },
      { text: 'Status', value: 'status', sortable: true }
    ];

    const filters = ref({
      startDate: '',
      endDate: '',
      status: '',
      minAmount: '',
      maxAmount: '',
      platform: '',
      instructor: ''
    });

    const instructors = ref([]);
    const loadingInstructors = ref(false);

    const hasActiveFilters = computed(() => {
      return Object.values(filters.value).some(value => value !== '');
    });

    const filteredPayments = computed(() => {
      let result = [...payments.value];
      
      // Apply search filter
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter(payment => {
          return (
            payment.id.toString().includes(query) ||
            payment.user.first_name.toLowerCase().includes(query) ||
            payment.user.last_name.toLowerCase().includes(query) ||
            payment.user.email.toLowerCase().includes(query) ||
            payment.item_type.toLowerCase().includes(query) ||
            payment.amount.toString().includes(query) ||
            payment.platform.toLowerCase().includes(query) ||
            payment.status.toLowerCase().includes(query)
          );
        });
      }

      // Apply sorting
      if (sortBy.value) {
        result.sort((a, b) => {
          let aValue = a[sortBy.value];
          let bValue = b[sortBy.value];

          // Handle nested user object
          if (sortBy.value === 'user') {
            aValue = `${a.user.first_name} ${a.user.last_name}`;
            bValue = `${b.user.first_name} ${b.user.last_name}`;
          }

          // Handle numeric values
          if (sortBy.value === 'amount') {
            aValue = parseFloat(aValue);
            bValue = parseFloat(bValue);
          }

          // Handle date values
          if (sortBy.value === 'purchase_date') {
            aValue = new Date(aValue);
            bValue = new Date(bValue);
          }

          if (sortType.value === 'asc') {
            return aValue > bValue ? 1 : -1;
          } else {
            return aValue < bValue ? 1 : -1;
          }
        });
      }

      return result;
    });

    const fetchPayments = async () => {
      loading.value = true;
      try {
        const params = {
          per_page: serverOptions.value.rowsPerPage,
          page: serverOptions.value.page
        };

        // Add filter parameters if they have values
        if (filters.value.startDate) params.start_date = filters.value.startDate;
        if (filters.value.endDate) params.end_date = filters.value.endDate;
        if (filters.value.status) params.status = filters.value.status;
        if (filters.value.minAmount) params.min_amount = filters.value.minAmount;
        if (filters.value.maxAmount) params.max_amount = filters.value.maxAmount;
        if (filters.value.platform) params.platform = filters.value.platform;
        if (filters.value.instructor) params.instructor_id = filters.value.instructor;

        const response = await axios.get('https://api.kewaar.com/api/admin/payments', {
          headers: {
            'Authorization': 'Bearer ' + localStorage.getItem('token'),
            'Content-Type': 'application/json'
          },
          params
        });
        
        if (response.data && response.data.success) {
          if (response.data.data && Array.isArray(response.data.data.data)) {
            payments.value = response.data.data.data;
            totalItems.value = response.data.data.total || response.data.data.pagination?.total || 0;
            serverOptions.value.page = response.data.data.current_page || 1;
          } else {
            console.error('Invalid data structure in response:', response.data);
            payments.value = [];
            totalItems.value = 0;
          }
        } else {
          console.error('Invalid response format:', response.data);
          payments.value = [];
          totalItems.value = 0;
        }
      } catch (error) {
        console.error('Error fetching payments:', error);
        payments.value = [];
        totalItems.value = 0;
      } finally {
        loading.value = false;
      }
    };

    const fetchInstructors = async () => {
      loadingInstructors.value = true;
      try {
        const response = await axios.get('https://api.kewaar.com/api/admin/instructors1', {
          headers: {
            'Authorization': 'Bearer ' + localStorage.getItem('token'),
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data && response.data.success) {
          if (response.data.data && Array.isArray(response.data.data.data)) {
            instructors.value = response.data.data.data;
          } else {
            console.error('Invalid instructors data structure:', response.data);
            instructors.value = [];
          }
        } else {
          console.error('Invalid instructors response format:', response.data);
          instructors.value = [];
        }
      } catch (error) {
        console.error('Error fetching instructors:', error);
        instructors.value = [];
      } finally {
        loadingInstructors.value = false;
      }
    };

    const toggleSelection = (payment) => {
      const index = selectedItems.value.findIndex(item => item.id === payment.id);
      if (index === -1) {
        selectedItems.value.push(payment);
      } else {
        selectedItems.value.splice(index, 1);
      }
    };

    const changePage = (newPage) => {
      serverOptions.value.page = newPage;
      fetchPayments();
    };

    const sortTable = (column) => {
      if (sortBy.value === column) {
        sortType.value = sortType.value === 'asc' ? 'desc' : 'asc';
      } else {
        sortBy.value = column;
        sortType.value = 'asc';
      }
    };

    const handleSearch = () => {
      // Reset to first page when searching
      serverOptions.value.page = 1;
    };

    const handlePerPageChange = () => {
      serverOptions.value.page = 1; // Reset to first page when changing items per page
      fetchPayments();
    };

    const applyFilters = () => {
      serverOptions.value.page = 1; // Reset to first page when applying filters
      fetchPayments();
    };

    const clearFilters = () => {
      filters.value = {
        startDate: '',
        endDate: '',
        status: '',
        minAmount: '',
        maxAmount: '',
        platform: '',
        instructor: ''
      };
      applyFilters();
    };

    onMounted(() => {
      fetchPayments();
      fetchInstructors();
    });

    return {
      payments,
      loading,
      headers,
      selectedItems,
      serverOptions,
      totalItems,
      searchQuery,
      sortBy,
      sortType,
      filteredPayments,
      toggleSelection,
      changePage,
      sortTable,
      handleSearch,
      handlePerPageChange,
      filters,
      hasActiveFilters,
      applyFilters,
      clearFilters,
      instructors,
      loadingInstructors
    };
  }
}
</script>

<style scoped>
.payments-container {
  padding: 2rem;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.header-section {
  margin-bottom: 2rem;
}

.header-section h2 {
  color: #2c3e50;
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
}

.stats-container {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  min-width: 150px;
}

.stat-label {
  color: #6c757d;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
}

.stat-value {
  color: #2c3e50;
  font-size: 1.5rem;
  font-weight: 600;
}

.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  padding: 1rem;
  overflow-x: auto;
}

.payments-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
}

.payments-table th {
  background-color: #f8f9fa;
  color: #2c3e50;
  font-weight: 600;
  text-align: left;
  padding: 1rem;
  border-bottom: 2px solid #e9ecef;
  font-size: 0.875rem;
}

.payments-table td {
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  color: #2c3e50;
  font-size: 0.875rem;
}

.payments-table tr:hover {
  background-color: #f8f9fa;
  cursor: pointer;
}

.payments-table tr.selected {
  background-color: #e3f2fd;
}

.loading-row, .empty-row {
  text-align: center;
  padding: 2rem !important;
  color: #6c757d;
}

.loading-spinner {
  display: inline-block;
  width: 1.5rem;
  height: 1.5rem;
  border: 3px solid #f3f3f3;
  border-radius: 50%;
  border-top: 3px solid #3498db;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.status-badge {
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
  display: inline-block;
}

.status-badge.completed {
  background-color: #d4edda;
  color: #155724;
}

.status-badge.pending {
  background-color: #fff3cd;
  color: #856404;
}

.status-badge.failed {
  background-color: #f8d7da;
  color: #721c24;
}

.user-info {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-weight: 500;
  color: #2c3e50;
}

.user-email {
  color: #6c757d;
  font-size: 0.75rem;
}

.amount-value {
  font-weight: 500;
  color: #2c3e50;
}

.date-value {
  color: #6c757d;
  font-size: 0.875rem;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
  flex-wrap: wrap;
  gap: 1rem;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.per-page-selector {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.per-page-selector label {
  color: #6c757d;
  font-size: 0.875rem;
}

.per-page-select {
  padding: 0.5rem;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background-color: white;
  color: #2c3e50;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.per-page-select:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
}

.pagination-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background-color: white;
  color: #2c3e50;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #f8f9fa;
  border-color: #dee2e6;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  color: #6c757d;
  font-size: 0.875rem;
}

.table-controls {
  margin-bottom: 1rem;
  display: flex;
  justify-content: flex-end;
}

.search-box {
  position: relative;
  width: 300px;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.sort-header {
  cursor: pointer;
  user-select: none;
  position: relative;
  padding-right: 1.5rem !important;
}

.sort-header:hover {
  background-color: #f1f3f5;
}

.sort-icon {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.75rem;
  color: #6c757d;
}

.sort-header.sorted .sort-icon {
  color: #3498db;
}

.filters-section {
  background: white;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.filter-group {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 200px;
}

.filter-item label {
  color: #2c3e50;
  font-size: 0.875rem;
  font-weight: 500;
}

.filter-input {
  padding: 0.5rem;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.filter-input:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
}

.date-range, .amount-range {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.date-range span, .amount-range span {
  color: #6c757d;
  font-size: 0.875rem;
}

.clear-filters-btn {
  padding: 0.5rem 1rem;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  color: #6c757d;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.clear-filters-btn:hover:not(:disabled) {
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.clear-filters-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-indicator {
  color: #6c757d;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

@media (max-width: 768px) {
  .payments-table {
    display: block;
  }
  
  .payments-table thead {
    display: none;
  }
  
  .payments-table tbody {
    display: block;
  }
  
  .payments-table tr {
    display: block;
    margin-bottom: 1rem;
    border: 1px solid #e9ecef;
    border-radius: 8px;
  }
  
  .payments-table td {
    display: block;
    text-align: right;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e9ecef;
  }
  
  .payments-table td:last-child {
    border-bottom: none;
  }
  
  .payments-table td::before {
    content: attr(data-label);
    float: left;
    font-weight: 600;
    color: #2c3e50;
  }
  
  .payments-table td:first-child {
    background-color: #f8f9fa;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  .table-controls {
    justify-content: center;
  }
  
  .search-box {
    width: 100%;
    max-width: 300px;
  }

  .pagination {
    flex-direction: column;
    align-items: center;
  }
  
  .per-page-selector {
    margin-top: 1rem;
  }

  .filter-group {
    flex-direction: column;
  }
  
  .filter-item {
    width: 100%;
  }
  
  .date-range, .amount-range {
    flex-direction: column;
    align-items: stretch;
  }
}
</style> 